import { AccountBalance, ArrowForward, Download, ExpandLess, ExpandMore } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { Buffer } from "buffer";
import saveAs from "file-saver";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { CustomButton } from "src/Components/Common/Button/CustomButton";
import useFetch from "src/Components/Common/useFetch";
import CustomToast from "src/Components/CustomToast";
import { API_ENDPOINTS } from "src/Utils/ApiConstants/ApiUrlConstants";
import { convertUtcToIst } from "src/Utils/DateUtils";
import {
  trackAccountPayableToggled,
  trackAccountReceivablesToggled,
  trackActionOnHoldsClicked,
  trackAPAdsVisibilityClicked,
  trackAPBrandPromoClicked,
  trackAPInvoicesClicked,
  trackAPOtherEntriesClicked,
  trackAPPaymentInProgressClicked,
  trackAPPaymentMadeClicked,
  trackAPPrRtvClicked,
  trackAPTdsClicked,
  trackARCreditNoteReceivedClicked,
  trackARInvoicesRaisedClicked,
  trackARPaymentReceivedClicked,
  trackCalculationToggled,
  trackClosingBalanceToggled,
  trackCreditsDebitsToggled,
  trackDueAdsVisibilityClicked,
  trackDueAdvancePaymentClicked,
  trackDueBrandPromosClicked,
  trackDueInvoicesToggled,
  trackDueOtherEntriesClicked,
  trackDuePayableToggled,
  trackDuePrRtvClicked,
  trackDueTdsClicked,
  trackHoldAdhocOtherClicked,
  trackHoldAdsVisibilityClicked,
  trackHoldBDPOClicked,
  trackHoldEventsClicked,
  trackHoldGSTClicked,
  trackHoldPrRtvClicked,
  trackHoldsOnPayableToggled,
  trackHoldsSectionViewed,
  trackLedgerDownloaded,
  trackLedgerFilterApplied,
  trackLedgerFromDateChanged,
  trackLedgerHoldBannerViewed,
  trackLedgerOrgChanged,
  trackLedgerSummaryViewed,
  trackLedgerToDateChanged,
  trackNeedsActionToggled,
  trackOtherIssuesPendingOnUsClicked,
  trackOtherIssuesPendingOnVendorClicked,
  trackOtherIssuesToggled,
  trackOverdueInvoicesClicked,
  trackReconciliationActionsToggled,
  trackReconPendingOnUsClicked,
  trackReconPendingOnVendorClicked,
  trackRejectedInvoiceAmountClicked,
  trackRejectedInvoicesToggled,
  trackResolveHoldsClicked,
} from "src/Utils/Mixpanel/VendorPortalEvents";
import { formatMoney, ToDineroObj } from "src/Utils/MoneyUtils";
import { useVendorPortal } from "./VendorPortalLayout";

export interface SummarisedARLedgerDataVendorPortalResponse {
  invoices_amount: number;
  invoices_entries: number;
  payments_amount: number;
  payments_entries: number;
  credit_notes_amount: number;
  credit_notes_entries: number;
  total_entries: number;
  net_amount: number;
}

export interface SummarisedAPLedgerDataVendorPortalResponse {
  invoices_amount: number;
  invoices_entries: number;
  payment_amount: number;
  payment_entries: number;
  pr_rtv_amount: number;
  pr_rtv_entries: number;
  promo_amount: number;
  promo_entries: number;
  tds_amount: number;
  tds_entries: number;
  other_amount: number;
  other_entries: number;
  total_entries: number;
  net_amount: number;
  // Not populated by the backend yet — WIP badge shown until these land.
  ads_visibility_amount?: number;
  ads_visibility_entries?: number;
  payment_in_progress_amount?: number;
  payment_in_progress_entries?: number;
}

interface HoldSummary {
  "GST Related Hold": number;
  "PR/RTV Related Hold": number;
  "Events Hold": number;
  "Ads Visibility Hold": number;
  "BDPO Hold": number;
  "Adhoc / Other Holds": number;
  isInHoldVendors: boolean;
}

interface DueAmountSummary {
  due_invoices_amount: number | string;
  due_invoices_entries: number | string;
  pr_rtv_amount: number | string;
  pr_rtv_entries: number | string;
  ads_visibility_amount: number | string;
  ads_visibility_entries: number | string;
  brand_promos_amount: number | string;
  brand_promos_entries: number | string;
  tds_amount: number | string;
  tds_entries: number | string;
  other_entries_amount: number;
  other_entries: number;
  advance_payments_amount: number;
  advance_payments_entries: number;
}

interface NeedsActionFromVendor {
  rejectedInvoices: { amount: number; count: number };
  actionOnHolds: { amount: number; count: number };
  totalAmount: number;
  totalCount: number;
}

interface ActionsPendingSummary {
  actionsPendingOnVendor: { amount: number; count: number };
  actionsPendingOnUs: { amount: number; count: number };
}

interface LedgerData {
  summarisedARData: SummarisedARLedgerDataVendorPortalResponse;
  summarisedAPData: SummarisedAPLedgerDataVendorPortalResponse;
  OpeningBalance: number;
  closingBalance: number;
  holdSummary?: HoldSummary;
  dueAmountSummary?: DueAmountSummary;
  duePayable?: number;
  needsActionFromVendor?: NeedsActionFromVendor;
  reconciliationActions?: ActionsPendingSummary;
  otherIssuesRaised?: ActionsPendingSummary;
}

const num = (value: number | string | undefined | null) => Number(value ?? 0);

const ZONE_TONES = {
  filtered: { bg: "#e8ecff", color: "#2b3fc4" },
  snapshot: { bg: "#eef1f4", color: "#4a5568" },
  action: { bg: "#fdecef", color: "#b02a4a" },
} as const;

// ─── Zone header: section title + a pill describing what the figures below are scoped to ──
const ZoneHeader = ({
  title,
  tone,
  scopeLabel,
}: {
  title: string;
  tone: keyof typeof ZONE_TONES;
  scopeLabel: string;
}) => (
  <Box sx={{ mt: 0.5, mb: 1.5, px: 0.25 }}>
    <Typography sx={{ fontSize: 18, fontWeight: 700, color: "rgba(0,0,0,0.87)", letterSpacing: "0.01em" }}>
      {title}
    </Typography>
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.875,
        mt: 0.625,
        px: 1.5,
        py: 0.5,
        borderRadius: 999,
        fontSize: 13.5,
        fontWeight: 600,
        backgroundColor: ZONE_TONES[tone].bg,
        color: ZONE_TONES[tone].color,
      }}
    >
      <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: "currentColor" }} />
      {scopeLabel}
    </Box>
  </Box>
);

// ─── Divider between zones, previewing the tone of the section it introduces ──
const SectionBreak = ({ label, note, tone }: { label: string; note: string; tone: "snapshot" | "action" }) => (
  <Box sx={{ mt: 2, mb: 1, px: 0.25 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Box sx={{ flex: 1, height: 2, borderRadius: 1, backgroundColor: tone === "snapshot" ? "#d4dae1" : "#f3ccd5" }} />
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.10em",
          textTransform: "uppercase",
          px: 1.5,
          py: 0.375,
          borderRadius: 999,
          whiteSpace: "nowrap",
          backgroundColor: ZONE_TONES[tone].bg,
          color: ZONE_TONES[tone].color,
        }}
      >
        {label} <span style={{ fontSize: 12 }}>↓</span>
      </Box>
      <Box sx={{ flex: 1, height: 2, borderRadius: 1, backgroundColor: tone === "snapshot" ? "#d4dae1" : "#f3ccd5" }} />
    </Box>
    <Typography sx={{ mt: 0.5, textAlign: "center", fontSize: 11.5, fontWeight: 500, color: "text.secondary" }}>
      {note}
    </Typography>
  </Box>
);

// ─── Expand/collapse affordance — a labelled pill instead of a bare chevron icon ──
const ExpandToggle = ({ expanded, onClick }: { expanded: boolean; onClick?: () => void }) => (
  <Box
    onClick={onClick}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.25,
      px: 1.25,
      py: 0.375,
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      whiteSpace: "nowrap",
      cursor: onClick ? "pointer" : "default",
      backgroundColor: expanded ? "rgba(0, 0, 0, 0.06)" : "rgba(61, 90, 254, 0.1)",
      color: expanded ? "rgba(0, 0, 0, 0.6)" : "#3d5afe",
    }}
  >
    {expanded ? "Hide" : "View"}
    {expanded ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
  </Box>
);

// ─── Badge for fields the backend hasn't populated yet ──
const WipBadge = () => (
  <Box
    component="span"
    sx={{
      display: "inline-block",
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: "#8a6d00",
      backgroundColor: "#fff3cd",
      borderRadius: "3px",
      px: 0.75,
      py: 0.25,
    }}
  >
    WIP
  </Box>
);

// ─── A single metric tile used inside subcards (leaf-grid equivalent) ──
const Leaf = ({
  label,
  amount,
  count,
  countSuffix,
  bold = false,
  onClick,
}: {
  label: string;
  amount: React.ReactNode;
  count?: number;
  countSuffix?: React.ReactNode;
  bold?: boolean;
  onClick?: () => void;
}) => (
  <Card
    onClick={onClick}
    sx={{
      flex: "1 1 200px",
      border: "1px solid #e4e4e4",
      cursor: onClick ? "pointer" : "default",
      "&:hover": onClick ? { backgroundColor: "#fafbff" } : undefined,
    }}
  >
    <CardContent sx={{ p: 1.25 }}>
      <Typography className="body-m-bold" sx={{ color: bold ? "rgba(0,0,0,0.87)" : "text.secondary", mb: 1 }}>
        {label}
      </Typography>
      <Typography className="body-l-bold" sx={{ color: bold ? "rgba(0,0,0,0.87)" : "#575757" }}>
        {amount}
      </Typography>
      {count !== undefined && (
        <Typography sx={{ fontSize: 13, fontWeight: 500, color: "text.secondary", mt: 0.5 }}>
          Count: {count}
          {countSuffix}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const LedgerSummaryVendorPortal: React.FC = () => {
  const { handleUnauthorizedError, isSessionReady, token, userPan } = useVendorPortal();
  const history = useHistory();
  const todayDate = new Date();
  const [selectedOrg, setSelectedOrg] = useState("All");
  const [allCompanies, setAllCompanies] = useState([]);

  // Calculate financial year dates
  const getFinancialYearStart = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth(); // 0-indexed (0 = January, 3 = April)

    // If current month is before April (0-3), financial year started previous year
    // If current month is April or after (3-11), financial year started this year
    const financialYearStart =
      currentMonth < 3
        ? new Date(currentYear - 1, 3, 1) // April 1st of previous year
        : new Date(currentYear, 3, 1); // April 1st of current year

    return financialYearStart;
  };

  const [fromDate, setFromDate] = useState<Date | null>(getFinancialYearStart());
  const [toDate, setToDate] = useState<Date | null>(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday;
  }); // Previous day's date

  const [fromDateUI, setFromDateUI] = useState<Date | null>(getFinancialYearStart());
  const [toDateUI, setToDateUI] = useState<Date | null>(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday;
  }); // Previous day's date

  const [ledgerData, setLedgerData] = useState<LedgerData | null>(null);
  const [expandedSections, setExpandedSections] = useState({
    closingBalance: false,
    duePayable: false,
    needsAction: false,
  });
  const [isDownReconSummaryReport, setIsDownReconSummaryReport] = useState(false);
  const [applyLoader, setApplyLoader] = useState(false);

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  useEffect(() => {
    // Only run when session is ready
    if (!isSessionReady) return;
    if (token) {
      // If token exists, use it to fetch data
      trackLedgerSummaryViewed();
      getSummarisedValues(token);
      getAllCompanies(token);
    } else {
      // If no token, show error
      toast.error(<CustomToast message="Session expired. Please log in again." />);
    }
  }, [isSessionReady, token]); // Depend on both isSessionReady and token

  useEffect(() => {
    if (ledgerData?.holdSummary?.isInHoldVendors) {
      trackLedgerHoldBannerViewed({
        reason: "GST Non-Compliance — GSTIN verification failed for last 2 filing periods",
      });
    }
  }, [ledgerData?.holdSummary?.isInHoldVendors]);

  const getAllCompanies = (token) => {
    useFetch(API_ENDPOINTS.FETCH_VP_LEDGER_SUMMARY_GET_ALL_COMPANIES.url, "GET", {
      failureMessage: API_ENDPOINTS.FETCH_VP_LEDGER_SUMMARY_GET_ALL_COMPANIES.failureMessage,
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
      thenCallBack: (response) => {
        setAllCompanies(response.data.data || []);
      },
      catchCallBack: (error) => {
        handleUnauthorizedError(error);
      },
    });
  };
  const getSummarisedValues = async (token) => {
    setApplyLoader(true);
    await useFetch(API_ENDPOINTS.FETCH_VP_LEDGER_SUMMARY_GET_SUMMARISED_VALUES.url, "GET", {
      failureMessage: API_ENDPOINTS.FETCH_VP_LEDGER_SUMMARY_GET_SUMMARISED_VALUES.failureMessage,
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          startDate: convertUtcToIst(fromDate, "YYYY-MM-DD"), // yyyy-MM-dd format
          toDate: convertUtcToIst(toDate, "YYYY-MM-DD"), // yyyy-MM-dd format
          companyId: selectedOrg === "All" ? null : selectedOrg,
        },
      },
      thenCallBack: (response) => {
        const data = response.data.data || null;
        // TODO: Remove dummy holds data once backend provides real holds data
        // if (data) {
        //   data.holdsData = {
        //     gst_related_hold_amount: 145000,
        //     gst_related_hold_entries: 7,
        //     pr_rtv_related_hold_amount: 89200,
        //     pr_rtv_related_hold_entries: 4,
        //     events_hold_amount: 67500,
        //     events_hold_entries: 6,
        //     ads_visibility_bdpo_hold_amount: 23800,
        //     ads_visibility_bdpo_hold_entries: 2,
        //     adhoc_other_hold_amount: 0,
        //     adhoc_other_hold_entries: 0,
        //     total_hold_amount: 325500,
        //     is_full_payment_on_hold: true,
        //   };
        // }
        setLedgerData(data);
        setApplyLoader(false);
      },
      catchCallBack: (error) => {
        setApplyLoader(false);
        handleUnauthorizedError(error);
      },
    });
  };

  const [holdsExpanded, setHoldsExpanded] = useState(false);
  const [calcExpanded, setCalcExpanded] = useState(true);
  const [creditsDebitsExpanded, setCreditsDebitsExpanded] = useState(false);
  const [dueInvoicesExpanded, setDueInvoicesExpanded] = useState(false);
  const [rejectedInvoicesExpanded, setRejectedInvoicesExpanded] = useState(false);
  const [reconciliationActionsExpanded, setReconciliationActionsExpanded] = useState(false);
  const [otherIssuesExpanded, setOtherIssuesExpanded] = useState(false);
  const [accountPayableExpanded, setAccountPayableExpanded] = useState(false);
  const [accountReceivablesExpanded, setAccountReceivablesExpanded] = useState(false);

  const holdBucketKeys = [
    "GST Related Hold",
    "PR/RTV Related Hold",
    "Events Hold",
    "Ads Visibility Hold",
    "BDPO Hold",
    "Adhoc / Other Holds",
  ] as const;
  const holdBucketClickTrackers: Record<typeof holdBucketKeys[number], (props: { tile_amount?: number }) => void> = {
    "GST Related Hold": trackHoldGSTClicked,
    "PR/RTV Related Hold": trackHoldPrRtvClicked,
    "Events Hold": trackHoldEventsClicked,
    "Ads Visibility Hold": trackHoldAdsVisibilityClicked,
    "BDPO Hold": trackHoldBDPOClicked,
    "Adhoc / Other Holds": trackHoldAdhocOtherClicked,
  };
  const totalHolds = ledgerData?.holdSummary
    ? holdBucketKeys.reduce((sum, k) => sum + num(ledgerData.holdSummary[k]), 0)
    : 0;
  const holdBucketCount = holdBucketKeys.length;
  const duePayable = ledgerData?.duePayable ?? 0;
  const creditsDebitsTotal = ledgerData?.dueAmountSummary
    ? num(ledgerData.dueAmountSummary.pr_rtv_amount) +
      num(ledgerData.dueAmountSummary.ads_visibility_amount) +
      num(ledgerData.dueAmountSummary.brand_promos_amount) +
      num(ledgerData.dueAmountSummary.tds_amount) +
      num(ledgerData.dueAmountSummary.other_entries_amount) +
      num(ledgerData.dueAmountSummary.advance_payments_amount)
    : 0;
  const dueInvoicesAmount = ledgerData?.dueAmountSummary ? num(ledgerData.dueAmountSummary.due_invoices_amount) : 0;
  const needsActionAmount =
    (ledgerData?.needsActionFromVendor?.totalAmount ?? 0) +
    (ledgerData?.reconciliationActions
      ? ledgerData.reconciliationActions.actionsPendingOnVendor.amount +
        ledgerData.reconciliationActions.actionsPendingOnUs.amount
      : 0) +
    (ledgerData?.otherIssuesRaised
      ? ledgerData.otherIssuesRaised.actionsPendingOnVendor.amount +
        ledgerData.otherIssuesRaised.actionsPendingOnUs.amount
      : 0);
  const needsActionCount =
    (ledgerData?.needsActionFromVendor?.totalCount ?? 0) +
    (ledgerData?.reconciliationActions
      ? ledgerData.reconciliationActions.actionsPendingOnVendor.count +
        ledgerData.reconciliationActions.actionsPendingOnUs.count
      : 0) +
    (ledgerData?.otherIssuesRaised
      ? ledgerData.otherIssuesRaised.actionsPendingOnVendor.count +
        ledgerData.otherIssuesRaised.actionsPendingOnUs.count
      : 0);

  const downloadLedgerReport = (token) => {
    setIsDownReconSummaryReport(true);
    useFetch(API_ENDPOINTS.FETCH_VP_LEDGER_SUMMARY_GET_DOWNLOAD_LEDGER_REPORT.url, "GET", {
      failureMessage: API_ENDPOINTS.FETCH_VP_LEDGER_SUMMARY_GET_DOWNLOAD_LEDGER_REPORT.failureMessage,
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          startDate: convertUtcToIst(fromDate, "YYYY-MM-DD"), // yyyy-MM-dd format
          toDate: convertUtcToIst(toDate, "YYYY-MM-DD"), // yyyy-MM-dd format
          companyId: selectedOrg === "All" ? null : selectedOrg,
        },
      },
      thenCallBack: (response) => {
        const filename = `${userPan}_${convertUtcToIst(fromDate, "DD-MM-YYYY")}_${convertUtcToIst(
          toDate,
          "DD-MM-YYYY"
        )}.xlsx`;
        const excelData = Buffer.from(response.data.data, "base64");
        const fileType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
        const blob = new Blob([excelData], { type: fileType });
        saveAs(blob, filename);
        trackLedgerDownloaded({
          date_range_days:
            fromDate && toDate ? Math.round((toDate.getTime() - fromDate.getTime()) / 86400000) : undefined,
          file_format: "xlsx",
        });
        setIsDownReconSummaryReport(false);
      },
      catchCallBack: (error) => {
        setIsDownReconSummaryReport(false);
        handleUnauthorizedError(error);
      },
    });
  };

  return (
    <Box sx={{ p: 1.25 }}>
      {/* Filters Row */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.5, gap: 1.25 }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <Autocomplete
            options={[
              { companyCode: "All", companyName: "All" },
              ...allCompanies.sort((a, b) => a.companyName.localeCompare(b.companyName)),
            ]}
            getOptionLabel={(option) => option.companyName || ""}
            value={
              allCompanies.find((company) => company.companyCode === selectedOrg) || {
                companyCode: "All",
                companyName: "All",
              }
            }
            onChange={(_event, newValue) => {
              const org = newValue?.companyCode || "All";
              setSelectedOrg(org);
              trackLedgerOrgChanged({ org_selected: org });
            }}
            sx={{ width: 250 }}
            size="small"
            renderInput={(params) => (
              <TextField {...params} label="Select Organisation Name" className="update-custom-border-radius" />
            )}
          />

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="From"
              value={fromDate}
              onChange={(newValue) => {
                setFromDate(newValue);
                trackLedgerFromDateChanged({ date: newValue?.toISOString?.().split("T")[0] });
              }}
              format="dd/MM/yyyy"
              slotProps={{
                textField: {
                  size: "small",
                  className: "update-custom-border-radius",
                  sx: { width: 150 },
                },
              }}
            />
            <DatePicker
              label="To"
              value={toDate}
              onChange={(newValue) => {
                setToDate(newValue);
                trackLedgerToDateChanged({ date: newValue?.toISOString?.().split("T")[0] });
              }}
              maxDate={(() => {
                const yesterday = new Date();
                yesterday.setDate(yesterday.getDate() - 1);
                return yesterday;
              })()}
              format="dd/MM/yyyy"
              slotProps={{
                textField: {
                  size: "small",
                  className: "update-custom-border-radius",
                  sx: { width: 150 },
                },
              }}
            />
          </LocalizationProvider>

          <CustomButton
            variant="contained"
            size="small"
            colorToken="violet"
            onClick={async () => {
              await getSummarisedValues(token);
              trackLedgerFilterApplied({
                org_selected: selectedOrg,
                from_date: fromDate?.toISOString?.().split("T")[0],
                to_date: toDate?.toISOString?.().split("T")[0],
                date_range_days:
                  fromDate && toDate ? Math.round((toDate.getTime() - fromDate.getTime()) / 86400000) : undefined,
              });
              setFromDateUI(fromDate);
              setToDateUI(toDate);
            }}
            startIcon={applyLoader && <CircularProgress size={20} sx={{ color: "grey" }} />}
            disabled={applyLoader}
          >
            Apply
          </CustomButton>
        </Box>

        <CustomButton
          variant="contained"
          size="small"
          colorToken="violet"
          startIcon={isDownReconSummaryReport ? <CircularProgress size={20} sx={{ color: "grey" }} /> : <Download />}
          disabled={isDownReconSummaryReport}
          onClick={() => downloadLedgerReport(token)}
        >
          Download Ledger
        </CustomButton>
      </Box>

      {/* Full Payment on Hold Alert Banner */}
      {ledgerData?.holdSummary?.isInHoldVendors && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 1.5,
            px: 2,
            py: 1.25,
            border: "1px solid #f5c6cb",
            borderRadius: 1,
            backgroundColor: "#fff5f5",
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 1.5,
              py: 0.5,
              borderRadius: 2,
              backgroundColor: "#e53935",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#fff" }} />
            <Typography variant="caption" sx={{ color: "#fff", fontWeight: 700, fontSize: "0.75rem" }}>
              Full Payment on Hold
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: "#c62828", fontWeight: 500 }}>
            Hold on payment has been put blocking payment. Reason:{" "}
            <strong>GST Non-Compliance — GSTIN verification failed for last 2 filing periods</strong>
          </Typography>
        </Box>
      )}

      {/* ============ ZONE 1 · LEDGER POSITION ============ */}
      {ledgerData && (
        <ZoneHeader
          title="Ledger Position"
          tone="filtered"
          scopeLabel={`Based on date filter · ${convertUtcToIst(fromDateUI, "DD/MM/YYYY")} – ${convertUtcToIst(
            toDateUI,
            "DD/MM/YYYY"
          )}`}
        />
      )}

      {/* Closing Balance Section */}
      {ledgerData ? (
        <Card sx={{ mb: 1.5, border: "1px solid #e0e0e0" }}>
          <CardContent sx={{ p: 1.25 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1,
                    backgroundColor: "#eceff1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <AccountBalance sx={{ color: "#607d8b", fontSize: 20 }} />
                </Box>
                <Typography className="heading-m">Closing Balance</Typography>
                <Typography className="body-m-medium" color="text.secondary">
                  as on {toDateUI ? convertUtcToIst(toDateUI, "DD/MM/YYYY") : ""}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography className="heading-m">{formatMoney(ToDineroObj(ledgerData.closingBalance))}</Typography>
                <ExpandToggle
                  expanded={expandedSections.closingBalance}
                  onClick={() => {
                    toggleSection("closingBalance");
                    trackClosingBalanceToggled(!expandedSections.closingBalance);
                  }}
                />
              </Box>
            </Box>

            {/* Collapsible Content */}
            {expandedSections.closingBalance && (
              <>
                {/* Opening Balance */}
                <Card sx={{ backgroundColor: "#fafafa", border: "1px solid rgba(0,0,0,0.12)", mb: 1.5 }}>
                  <CardContent sx={{ p: 1.5 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography className="heading-s">Opening Balance</Typography>
                        <Typography className="body-m-medium" color="text.secondary">
                          as on {fromDateUI ? convertUtcToIst(new Date(fromDateUI.getTime()), "DD/MM/YYYY") : ""}
                        </Typography>
                      </Box>
                      <Typography className="heading-s" color="text.secondary">
                        {formatMoney(ToDineroObj(ledgerData.OpeningBalance))}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>

                {/* Account Payable Section */}
                <Card sx={{ backgroundColor: "#fafafa", border: "1px solid rgba(0,0,0,0.12)", mb: 1.5 }}>
                  <CardContent sx={{ p: 1.5 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        setAccountPayableExpanded((prev) => !prev);
                        trackAccountPayableToggled(!accountPayableExpanded);
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography className="heading-s">Account Payable</Typography>
                        <Typography className="body-m-medium" color="text.secondary">
                          {convertUtcToIst(fromDateUI, "DD/MM/YYYY")} - {convertUtcToIst(toDateUI, "DD/MM/YYYY")}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography className="heading-s" color="text.secondary">
                          {formatMoney(ToDineroObj(ledgerData.summarisedAPData.net_amount))} (
                          {ledgerData.summarisedAPData.total_entries})
                        </Typography>
                        <ExpandToggle expanded={accountPayableExpanded} />
                      </Box>
                    </Box>

                    {accountPayableExpanded && (
                      <>
                        {/* Account Payable Cards */}
                        <Stack direction="row" spacing={1.25} sx={{ mt: 1.5, mb: 1.5 }}>
                          <Card
                            sx={{
                              flex: 1,
                              border: "1px solid #e4e4e4",
                              cursor: "pointer",
                              "&:hover": { backgroundColor: "#fafbff" },
                            }}
                            onClick={() =>
                              trackAPInvoicesClicked({
                                tile_amount: ledgerData.summarisedAPData.invoices_amount,
                                entry_count: ledgerData.summarisedAPData.invoices_entries,
                              })
                            }
                          >
                            <CardContent sx={{ p: 1.25 }}>
                              <Typography className="body-m-bold" color="text.secondary" sx={{ mb: 1 }}>
                                Invoices
                              </Typography>
                              <Typography className="body-l-bold" color="#575757">
                                {formatMoney(ToDineroObj(ledgerData.summarisedAPData.invoices_amount))} (
                                {ledgerData.summarisedAPData.invoices_entries})
                              </Typography>
                            </CardContent>
                          </Card>
                          <Card
                            sx={{
                              flex: 1,
                              border: "1px solid #e4e4e4",
                              cursor: "pointer",
                              "&:hover": { backgroundColor: "#fafbff" },
                            }}
                            onClick={() =>
                              trackAPPaymentMadeClicked({
                                tile_amount: ledgerData.summarisedAPData.payment_amount,
                                entry_count: ledgerData.summarisedAPData.payment_entries,
                              })
                            }
                          >
                            <CardContent sx={{ p: 1.25 }}>
                              <Typography className="body-m-bold" color="text.secondary" sx={{ mb: 1 }}>
                                Payment Made
                              </Typography>
                              <Typography className="body-l-bold" color="#575757">
                                {formatMoney(ToDineroObj(ledgerData.summarisedAPData.payment_amount))} (
                                {ledgerData.summarisedAPData.payment_entries})
                              </Typography>
                            </CardContent>
                          </Card>
                          <Card
                            sx={{
                              flex: 1,
                              border: "1px solid #e4e4e4",
                              cursor: "pointer",
                              "&:hover": { backgroundColor: "#fafbff" },
                            }}
                            onClick={() =>
                              trackAPPrRtvClicked({
                                tile_amount: ledgerData.summarisedAPData.pr_rtv_amount,
                                entry_count: ledgerData.summarisedAPData.pr_rtv_entries,
                              })
                            }
                          >
                            <CardContent sx={{ p: 1.25 }}>
                              <Typography className="body-m-bold" color="text.secondary" sx={{ mb: 1 }}>
                                PR/RTV
                              </Typography>
                              <Typography className="body-l-bold" color="#575757">
                                {formatMoney(ToDineroObj(ledgerData.summarisedAPData.pr_rtv_amount))} (
                                {ledgerData.summarisedAPData.pr_rtv_entries})
                              </Typography>
                            </CardContent>
                          </Card>
                          <Card
                            sx={{
                              flex: 1,
                              border: "1px solid #e4e4e4",
                              cursor: "pointer",
                              "&:hover": { backgroundColor: "#fafbff" },
                            }}
                            onClick={() =>
                              trackAPBrandPromoClicked({
                                tile_amount: ledgerData.summarisedAPData.promo_amount,
                                entry_count: ledgerData.summarisedAPData.promo_entries,
                              })
                            }
                          >
                            <CardContent sx={{ p: 1.25 }}>
                              <Typography className="body-m-bold" color="text.secondary" sx={{ mb: 1 }}>
                                Brand Promo (BDPO)
                              </Typography>
                              <Typography className="body-l-bold" color="#575757">
                                {formatMoney(ToDineroObj(ledgerData.summarisedAPData.promo_amount))} (
                                {ledgerData.summarisedAPData.promo_entries})
                              </Typography>
                            </CardContent>
                          </Card>
                        </Stack>

                        <Stack direction="row" spacing={1.25}>
                          <Card
                            sx={{
                              flex: 1,
                              border: "1px solid #e4e4e4",
                              cursor: "pointer",
                              "&:hover": { backgroundColor: "#fafbff" },
                            }}
                            onClick={() =>
                              trackAPPaymentInProgressClicked({
                                tile_amount: ledgerData.summarisedAPData.payment_in_progress_amount,
                                entry_count: ledgerData.summarisedAPData.payment_in_progress_entries,
                              })
                            }
                          >
                            <CardContent sx={{ p: 1.25 }}>
                              <Typography className="body-m-bold" color="text.secondary" sx={{ mb: 1 }}>
                                Payment in Progress
                              </Typography>
                              <Typography className="body-l-bold" color="#575757">
                                {ledgerData.summarisedAPData.payment_in_progress_amount !== undefined ? (
                                  <>
                                    {formatMoney(ToDineroObj(ledgerData.summarisedAPData.payment_in_progress_amount))} (
                                    {ledgerData.summarisedAPData.payment_in_progress_entries})
                                  </>
                                ) : (
                                  <WipBadge />
                                )}
                              </Typography>
                            </CardContent>
                          </Card>
                          <Card
                            sx={{
                              flex: 1,
                              border: "1px solid #e4e4e4",
                              cursor: "pointer",
                              "&:hover": { backgroundColor: "#fafbff" },
                            }}
                            onClick={() =>
                              trackAPAdsVisibilityClicked({
                                tile_amount: ledgerData.summarisedAPData.ads_visibility_amount,
                                entry_count: ledgerData.summarisedAPData.ads_visibility_entries,
                              })
                            }
                          >
                            <CardContent sx={{ p: 1.25 }}>
                              <Typography className="body-m-bold" color="text.secondary" sx={{ mb: 1 }}>
                                Ads &amp; Visibility
                              </Typography>
                              <Typography className="body-l-bold" color="#575757">
                                {ledgerData.summarisedAPData.ads_visibility_amount !== undefined ? (
                                  <>
                                    {formatMoney(ToDineroObj(ledgerData.summarisedAPData.ads_visibility_amount))} (
                                    {ledgerData.summarisedAPData.ads_visibility_entries})
                                  </>
                                ) : (
                                  <WipBadge />
                                )}
                              </Typography>
                            </CardContent>
                          </Card>
                          <Card
                            sx={{
                              flex: 1,
                              border: "1px solid #e4e4e4",
                              cursor: "pointer",
                              "&:hover": { backgroundColor: "#fafbff" },
                            }}
                            onClick={() =>
                              trackAPTdsClicked({
                                tile_amount: ledgerData.summarisedAPData.tds_amount,
                                entry_count: ledgerData.summarisedAPData.tds_entries,
                              })
                            }
                          >
                            <CardContent sx={{ p: 1.25 }}>
                              <Typography className="body-m-bold" color="text.secondary" sx={{ mb: 1 }}>
                                TDS
                              </Typography>
                              <Typography className="body-l-bold" color="#575757">
                                {formatMoney(ToDineroObj(ledgerData.summarisedAPData.tds_amount))} (
                                {ledgerData.summarisedAPData.tds_entries})
                              </Typography>
                            </CardContent>
                          </Card>
                          <Card
                            sx={{
                              flex: 1,
                              border: "1px solid #e4e4e4",
                              cursor: "pointer",
                              "&:hover": { backgroundColor: "#fafbff" },
                            }}
                            onClick={() =>
                              trackAPOtherEntriesClicked({ tile_amount: ledgerData.summarisedAPData.other_amount })
                            }
                          >
                            <CardContent sx={{ p: 1.25 }}>
                              <Typography className="body-m-bold" color="text.secondary" sx={{ mb: 1 }}>
                                Other entries
                              </Typography>
                              <Typography className="body-l-bold" color="#575757">
                                {formatMoney(ToDineroObj(ledgerData.summarisedAPData.other_amount))}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Stack>
                      </>
                    )}
                  </CardContent>
                </Card>

                {/* Account Receivables Section */}
                <Card sx={{ backgroundColor: "#fafafa", border: "1px solid rgba(0,0,0,0.12)" }}>
                  <CardContent sx={{ p: 1.5 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        setAccountReceivablesExpanded((prev) => !prev);
                        trackAccountReceivablesToggled(!accountReceivablesExpanded);
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography className="heading-s">Account Receivables</Typography>
                        <Typography className="body-m-medium" color="text.secondary">
                          {convertUtcToIst(fromDateUI, "DD/MM/YYYY")} - {convertUtcToIst(toDateUI, "DD/MM/YYYY")}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography className="heading-s" color="text.secondary">
                          {formatMoney(ToDineroObj(ledgerData.summarisedARData.net_amount))} (
                          {ledgerData.summarisedARData.total_entries})
                        </Typography>
                        <ExpandToggle expanded={accountReceivablesExpanded} />
                      </Box>
                    </Box>

                    {accountReceivablesExpanded && (
                      <Stack direction="row" spacing={1.25} sx={{ mt: 1.5 }}>
                        <Card
                          sx={{
                            flex: 1,
                            border: "1px solid #e4e4e4",
                            cursor: "pointer",
                            "&:hover": { backgroundColor: "#fafbff" },
                          }}
                          onClick={() =>
                            trackARInvoicesRaisedClicked({
                              tile_amount: ledgerData.summarisedARData.invoices_amount,
                              entry_count: ledgerData.summarisedARData.invoices_entries,
                            })
                          }
                        >
                          <CardContent sx={{ p: 1.25 }}>
                            <Typography className="body-m-bold" color="text.secondary" sx={{ mb: 1 }}>
                              Invoices raised
                            </Typography>
                            <Typography className="body-l-bold" color="#575757">
                              {formatMoney(ToDineroObj(ledgerData.summarisedARData.invoices_amount))} (
                              {ledgerData.summarisedARData.invoices_entries})
                            </Typography>
                          </CardContent>
                        </Card>
                        <Card
                          sx={{
                            flex: 1,
                            border: "1px solid #e4e4e4",
                            cursor: "pointer",
                            "&:hover": { backgroundColor: "#fafbff" },
                          }}
                          onClick={() =>
                            trackARPaymentReceivedClicked({
                              tile_amount: ledgerData.summarisedARData.payments_amount,
                              entry_count: ledgerData.summarisedARData.payments_entries,
                            })
                          }
                        >
                          <CardContent sx={{ p: 1.25 }}>
                            <Typography className="body-m-bold" color="text.secondary" sx={{ mb: 1 }}>
                              Payment received
                            </Typography>
                            <Typography className="body-l-bold" color="#575757">
                              {formatMoney(ToDineroObj(ledgerData.summarisedARData.payments_amount))} (
                              {ledgerData.summarisedARData.payments_entries})
                            </Typography>
                          </CardContent>
                        </Card>
                        <Card
                          sx={{
                            flex: 1,
                            border: "1px solid #e4e4e4",
                            cursor: "pointer",
                            "&:hover": { backgroundColor: "#fafbff" },
                          }}
                          onClick={() =>
                            trackARCreditNoteReceivedClicked({
                              tile_amount: ledgerData.summarisedARData.credit_notes_amount,
                              entry_count: ledgerData.summarisedARData.credit_notes_entries,
                            })
                          }
                        >
                          <CardContent sx={{ p: 1.25 }}>
                            <Typography className="body-m-bold" color="text.secondary" sx={{ mb: 1 }}>
                              Credit note received
                            </Typography>
                            <Typography className="body-l-bold" color="#575757">
                              {formatMoney(ToDineroObj(ledgerData.summarisedARData.credit_notes_amount))} (
                              {ledgerData.summarisedARData.credit_notes_entries})
                            </Typography>
                          </CardContent>
                        </Card>
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        <></>
      )}

      {ledgerData && (
        <SectionBreak
          label="Current Dues"
          tone="action"
          note="Due figures reflect your latest ledger position and don't change with the date filter above."
        />
      )}

      {/* ============ ZONE 2 · DUE POSITION ============ */}
      {ledgerData && (
        <ZoneHeader
          title="Due Position"
          tone="filtered"
          scopeLabel={`Fixed snapshot · as on ${convertUtcToIst(todayDate, "DD/MM/YYYY")} (not based on date filter)`}
        />
      )}

      {ledgerData ? (
        <Card sx={{ mb: 1.5, border: "1px solid #e0e0e0" }}>
          <CardContent sx={{ p: 1.25 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 2,
                cursor: "pointer",
              }}
              onClick={() => {
                toggleSection("duePayable");
                trackDuePayableToggled(!expandedSections.duePayable);
                if (!expandedSections.duePayable)
                  trackHoldsSectionViewed({ total_hold_amount: totalHolds, hold_buckets_with_data: holdBucketCount });
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography className="heading-m">Due Payable (- ve) / Receivable (+ ve)</Typography>
                  {ledgerData.holdSummary?.isInHoldVendors && (
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        px: 1.5,
                        py: 0.25,
                        borderRadius: 2,
                        border: "1px solid #e53935",
                        backgroundColor: "#fff",
                      }}
                    >
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#e53935" }} />
                      <Typography variant="caption" sx={{ color: "#e53935", fontWeight: 600, fontSize: "0.75rem" }}>
                        Full Payment on Hold
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Typography className="body-m-medium" color="text.secondary">
                  What you're going to get, after holds · due as on {convertUtcToIst(todayDate, "DD/MM/YYYY")}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography className="heading-m">{formatMoney(ToDineroObj(duePayable))}</Typography>
                <ExpandToggle expanded={expandedSections.duePayable} />
              </Box>
            </Box>

            {expandedSections.duePayable && (
              <>
                {/* Calculation toggle: Closing Balance − Holds on Payable = Due Payable */}
                <Box
                  sx={{
                    mt: 1.25,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 1,
                    cursor: "pointer",
                    fontSize: 13.5,
                    fontWeight: 600,
                    color: "#3d5afe",
                  }}
                  onClick={() => {
                    setCalcExpanded((prev) => !prev);
                    trackCalculationToggled(!calcExpanded);
                  }}
                >
                  <ExpandMore
                    fontSize="small"
                    sx={{ transition: "transform 0.18s ease", transform: calcExpanded ? "rotate(180deg)" : "none" }}
                  />
                  View calculation
                </Box>

                {calcExpanded && (
                  <Box
                    sx={{
                      mt: 1.25,
                      backgroundColor: "#fafafa",
                      border: "1px solid rgba(0,0,0,0.12)",
                      borderRadius: 1,
                      p: "16px 20px",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.375 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.secondary" }}>
                          Due Invoices
                        </Typography>
                        <Typography sx={{ fontSize: 17, fontWeight: 600 }}>
                          {formatMoney(ToDineroObj(Math.abs(dueInvoicesAmount)))}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: 24, fontWeight: 600, color: "text.secondary", lineHeight: 1 }}>
                        −
                      </Typography>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.375 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.secondary" }}>
                          Credits/Debits Yet to be Adjusted
                        </Typography>
                        <Typography sx={{ fontSize: 17, fontWeight: 600 }}>
                          {formatMoney(ToDineroObj(creditsDebitsTotal))}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: 24, fontWeight: 600, color: "text.secondary", lineHeight: 1 }}>
                        −
                      </Typography>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.375 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "rgba(0,0,0,0.87)" }}>
                          Holds on Payable
                        </Typography>
                        <Typography sx={{ fontSize: 17, fontWeight: 700 }}>
                          {formatMoney(ToDineroObj(totalHolds))}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: 24, fontWeight: 600, color: "text.secondary", lineHeight: 1 }}>
                        =
                      </Typography>
                      <Box
                        sx={{ display: "flex", flexDirection: "column", gap: 0.375, ml: "auto", textAlign: "right" }}
                      >
                        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                          {duePayable < 0 ? "Due Payable" : "Due Receivable"}
                        </Typography>
                        <Typography sx={{ fontSize: 21, fontWeight: 700 }}>
                          {formatMoney(ToDineroObj(duePayable))}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography
                      sx={{
                        mt: 1.25,
                        pt: 1.25,
                        borderTop: "1px dashed rgba(0,0,0,0.12)",
                        fontSize: 12,
                        color: "text.secondary",
                      }}
                    >
                      Due Invoices is shown as a negative figure in the ledger (−
                      {formatMoney(ToDineroObj(Math.abs(dueInvoicesAmount)))}); its magnitude is used in this
                      calculation.
                    </Typography>
                  </Box>
                )}

                {/* Due Invoices subcard */}
                <Card sx={{ backgroundColor: "#fafafa", border: "1px solid rgba(0,0,0,0.12)", mt: 1 }}>
                  <CardContent sx={{ p: 1.5 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "baseline",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        setDueInvoicesExpanded((prev) => !prev);
                        trackDueInvoicesToggled(!dueInvoicesExpanded);
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography className="heading-s">Due Invoices</Typography>
                        <Typography className="body-m-medium" color="text.secondary">
                          due as on {convertUtcToIst(todayDate, "DD/MM/YYYY")}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography className="heading-s">
                          {formatMoney(ToDineroObj(-Math.abs(dueInvoicesAmount)))} (
                          {ledgerData.dueAmountSummary ? num(ledgerData.dueAmountSummary.due_invoices_entries) : 0})
                        </Typography>
                        <ExpandToggle expanded={dueInvoicesExpanded} />
                      </Box>
                    </Box>
                    {dueInvoicesExpanded && (
                      <Stack direction="row" spacing={1.25} sx={{ mt: 1.5 }}>
                        <Box sx={{ flex: "0 1 320px" }}>
                          <Leaf
                            label="Overdue Invoices"
                            amount={formatMoney(ToDineroObj(-Math.abs(dueInvoicesAmount)))}
                            count={
                              ledgerData.dueAmountSummary
                                ? num(ledgerData.dueAmountSummary.due_invoices_entries)
                                : undefined
                            }
                            onClick={() =>
                              trackOverdueInvoicesClicked({
                                tile_amount: Math.abs(dueInvoicesAmount),
                                entry_count: ledgerData.dueAmountSummary
                                  ? num(ledgerData.dueAmountSummary.due_invoices_entries)
                                  : undefined,
                              })
                            }
                          />
                        </Box>
                      </Stack>
                    )}
                  </CardContent>
                </Card>

                {/* Credits/Debits Yet to be Adjusted subcard */}
                <Card sx={{ backgroundColor: "#fafafa", border: "1px solid rgba(0,0,0,0.12)", mt: 1 }}>
                  <CardContent sx={{ p: 1.5 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "baseline",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        setCreditsDebitsExpanded((prev) => !prev);
                        trackCreditsDebitsToggled(!creditsDebitsExpanded);
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography className="heading-s">Credits/Debits Yet to be Adjusted</Typography>
                        <Typography className="body-m-medium" color="text.secondary">
                          as on {convertUtcToIst(todayDate, "DD/MM/YYYY")}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography className="heading-s">
                          {formatMoney(ToDineroObj(creditsDebitsTotal))} (
                          {ledgerData.dueAmountSummary
                            ? num(ledgerData.dueAmountSummary.pr_rtv_entries) +
                              num(ledgerData.dueAmountSummary.ads_visibility_entries) +
                              num(ledgerData.dueAmountSummary.brand_promos_entries) +
                              num(ledgerData.dueAmountSummary.tds_entries) +
                              num(ledgerData.dueAmountSummary.other_entries) +
                              num(ledgerData.dueAmountSummary.advance_payments_entries)
                            : 0}
                          )
                        </Typography>
                        <ExpandToggle expanded={creditsDebitsExpanded} />
                      </Box>
                    </Box>

                    {creditsDebitsExpanded && (
                      <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap sx={{ mt: 1.25 }}>
                        <Leaf
                          label="PR/RTV"
                          amount={formatMoney(ToDineroObj(num(ledgerData.dueAmountSummary?.pr_rtv_amount)))}
                          count={num(ledgerData.dueAmountSummary?.pr_rtv_entries)}
                          onClick={() =>
                            trackDuePrRtvClicked({
                              tile_amount: num(ledgerData.dueAmountSummary?.pr_rtv_amount),
                              entry_count: num(ledgerData.dueAmountSummary?.pr_rtv_entries),
                            })
                          }
                        />
                        <Leaf
                          label="Ads & Visibility"
                          amount={formatMoney(ToDineroObj(num(ledgerData.dueAmountSummary?.ads_visibility_amount)))}
                          count={num(ledgerData.dueAmountSummary?.ads_visibility_entries)}
                          onClick={() =>
                            trackDueAdsVisibilityClicked({
                              tile_amount: num(ledgerData.dueAmountSummary?.ads_visibility_amount),
                              entry_count: num(ledgerData.dueAmountSummary?.ads_visibility_entries),
                            })
                          }
                        />
                        <Leaf
                          label="Brand Promos (BDPO)"
                          amount={formatMoney(ToDineroObj(num(ledgerData.dueAmountSummary?.brand_promos_amount)))}
                          count={num(ledgerData.dueAmountSummary?.brand_promos_entries)}
                          onClick={() =>
                            trackDueBrandPromosClicked({
                              tile_amount: num(ledgerData.dueAmountSummary?.brand_promos_amount),
                              entry_count: num(ledgerData.dueAmountSummary?.brand_promos_entries),
                            })
                          }
                        />
                        <Leaf
                          label="TDS"
                          amount={formatMoney(ToDineroObj(num(ledgerData.dueAmountSummary?.tds_amount)))}
                          count={num(ledgerData.dueAmountSummary?.tds_entries)}
                          onClick={() =>
                            trackDueTdsClicked({
                              tile_amount: num(ledgerData.dueAmountSummary?.tds_amount),
                              entry_count: num(ledgerData.dueAmountSummary?.tds_entries),
                            })
                          }
                        />
                        <Leaf
                          label="Other Entries"
                          amount={formatMoney(ToDineroObj(num(ledgerData.dueAmountSummary?.other_entries_amount)))}
                          count={num(ledgerData.dueAmountSummary?.other_entries)}
                          onClick={() =>
                            trackDueOtherEntriesClicked({
                              tile_amount: num(ledgerData.dueAmountSummary?.other_entries_amount),
                              entry_count: num(ledgerData.dueAmountSummary?.other_entries),
                            })
                          }
                        />
                        <Leaf
                          label="Advance Payment (to be adjusted)"
                          amount={formatMoney(ToDineroObj(num(ledgerData.dueAmountSummary?.advance_payments_amount)))}
                          count={num(ledgerData.dueAmountSummary?.advance_payments_entries)}
                          onClick={() =>
                            trackDueAdvancePaymentClicked({
                              tile_amount: num(ledgerData.dueAmountSummary?.advance_payments_amount),
                              entry_count: num(ledgerData.dueAmountSummary?.advance_payments_entries),
                            })
                          }
                        />
                      </Stack>
                    )}
                  </CardContent>
                </Card>

                {/* Holds on Payable subcard */}
                <Card sx={{ backgroundColor: "#fafafa", border: "1px solid rgba(0,0,0,0.12)", mt: 1 }}>
                  <CardContent sx={{ p: 1.5 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "baseline",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        setHoldsExpanded((prev) => !prev);
                        trackHoldsOnPayableToggled(!holdsExpanded);
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography className="heading-s">Holds on Payable</Typography>
                        <Typography className="body-m-medium" color="text.secondary">
                          as on {convertUtcToIst(todayDate, "DD/MM/YYYY")}
                        </Typography>
                        <Button
                          size="small"
                          variant="contained"
                          endIcon={<ArrowForward sx={{ fontSize: 14 }} />}
                          sx={{
                            borderRadius: 0,
                            textTransform: "none",
                            fontSize: 11.5,
                            lineHeight: 1.4,
                            px: 1,
                            py: 0.25,
                            minWidth: 0,
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            trackResolveHoldsClicked({ tile_amount: totalHolds });
                            history.push("/vendorPortal/hold-resolution");
                          }}
                        >
                          Resolve Holds
                        </Button>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography className="heading-s">{formatMoney(ToDineroObj(totalHolds))}</Typography>
                        <ExpandToggle expanded={holdsExpanded} />
                      </Box>
                    </Box>
                    <Typography sx={{ mt: 0.5, fontSize: 12, fontWeight: 600, color: "rgba(0,0,0,0.87)" }}>
                      In this PR/RTV, Ads Visibility, BDPO, Event Holds are entries yet to be accounted and pending
                      confirmation
                    </Typography>

                    {holdsExpanded && (
                      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1.5, mt: 1.25 }}>
                        {holdBucketKeys.map((key) => (
                          <Leaf
                            key={key}
                            label={key}
                            bold
                            amount={formatMoney(ToDineroObj(num(ledgerData.holdSummary?.[key])))}
                            onClick={() => {
                              holdBucketClickTrackers[key]({ tile_amount: num(ledgerData.holdSummary?.[key]) });
                              history.push("/vendorPortal/hold-resolution");
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </CardContent>
        </Card>
      ) : null}

      {ledgerData && (
        <SectionBreak
          label="Your Action Needed"
          tone="action"
          note="These items are waiting on you — acting on them moves money forward."
        />
      )}

      {/* ============ ZONE 3 · NEEDS ACTION FROM VENDOR TO PROCESS ============ */}
      {ledgerData && (
        <ZoneHeader
          title="Actions Needed/Pending from Vendor"
          tone="action"
          scopeLabel={`Requires your action to move forward · as on ${
            toDateUI ? convertUtcToIst(toDateUI, "DD/MM/YYYY") : ""
          }`}
        />
      )}

      {ledgerData ? (
        <Card sx={{ mb: 1.5, border: "1px solid #e0e0e0" }}>
          <CardContent sx={{ p: 1.25 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 2,
                cursor: "pointer",
              }}
              onClick={() => {
                toggleSection("needsAction");
                trackNeedsActionToggled(!expandedSections.needsAction);
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Typography className="heading-m">Needs Action</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography className="heading-m">
                  {formatMoney(ToDineroObj(needsActionAmount))} ({needsActionCount})
                </Typography>
                <ExpandToggle expanded={expandedSections.needsAction} />
              </Box>
            </Box>

            {expandedSections.needsAction && (
              <Card sx={{ backgroundColor: "#fafafa", border: "1px solid rgba(0,0,0,0.12)", mt: 1.5 }}>
                <CardContent sx={{ p: 1.5 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "baseline",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      setRejectedInvoicesExpanded((prev) => !prev);
                      trackRejectedInvoicesToggled(!rejectedInvoicesExpanded);
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography className="heading-s">Rejected invoices and holds awaiting action</Typography>
                      <Typography className="body-m-medium" color="text.secondary">
                        as on {toDateUI ? convertUtcToIst(toDateUI, "DD/MM/YYYY") : ""}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography className="heading-s">
                        {formatMoney(
                          ToDineroObj(
                            (ledgerData.needsActionFromVendor?.rejectedInvoices.amount ?? 0) +
                              (ledgerData.needsActionFromVendor?.actionOnHolds.amount ?? totalHolds)
                          )
                        )}{" "}
                        (
                        {(ledgerData.needsActionFromVendor?.rejectedInvoices.count ?? 0) +
                          (ledgerData.needsActionFromVendor?.actionOnHolds.count ?? holdBucketCount)}
                        )
                      </Typography>
                      <ExpandToggle expanded={rejectedInvoicesExpanded} />
                    </Box>
                  </Box>
                  {rejectedInvoicesExpanded && (
                    <Stack direction="row" spacing={1.25} sx={{ mt: 1.5 }}>
                      <Leaf
                        label="Rejected Invoice Amount"
                        amount={formatMoney(
                          ToDineroObj(ledgerData.needsActionFromVendor?.rejectedInvoices.amount ?? 0)
                        )}
                        count={ledgerData.needsActionFromVendor?.rejectedInvoices.count}
                        onClick={() => {
                          trackRejectedInvoiceAmountClicked({
                            tile_amount: ledgerData.needsActionFromVendor?.rejectedInvoices.amount ?? 0,
                            entry_count: ledgerData.needsActionFromVendor?.rejectedInvoices.count ?? 0,
                          });
                          history.push("/vendorPortal/invoice-rejection");
                        }}
                      />
                      <Leaf
                        label="Action on Holds"
                        amount={formatMoney(
                          ToDineroObj(ledgerData.needsActionFromVendor?.actionOnHolds.amount ?? totalHolds)
                        )}
                        count={ledgerData.needsActionFromVendor?.actionOnHolds.count ?? holdBucketCount}
                        onClick={() => {
                          trackActionOnHoldsClicked({ tile_amount: totalHolds });
                          history.push("/vendorPortal/hold-resolution");
                        }}
                      />
                    </Stack>
                  )}
                </CardContent>
              </Card>
            )}

            {expandedSections.needsAction && (
              <Card sx={{ backgroundColor: "#fafafa", border: "1px solid rgba(0,0,0,0.12)", mt: 1.5 }}>
                <CardContent sx={{ p: 1.5 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "baseline",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      setReconciliationActionsExpanded((prev) => !prev);
                      trackReconciliationActionsToggled(!reconciliationActionsExpanded);
                    }}
                  >
                    <Typography className="heading-s">Reconciliation Actions</Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {ledgerData.reconciliationActions ? (
                        <Typography className="heading-s">
                          {formatMoney(
                            ToDineroObj(
                              ledgerData.reconciliationActions.actionsPendingOnVendor.amount +
                                ledgerData.reconciliationActions.actionsPendingOnUs.amount
                            )
                          )}{" "}
                          (
                          {ledgerData.reconciliationActions.actionsPendingOnVendor.count +
                            ledgerData.reconciliationActions.actionsPendingOnUs.count}
                          )
                        </Typography>
                      ) : (
                        <WipBadge />
                      )}
                      <ExpandToggle expanded={reconciliationActionsExpanded} />
                    </Box>
                  </Box>
                  {reconciliationActionsExpanded && (
                    <Stack direction="row" spacing={1.25} sx={{ mt: 1.5 }}>
                      <Leaf
                        label="Actions Pending on Vendor"
                        amount={
                          ledgerData.reconciliationActions ? (
                            formatMoney(ToDineroObj(ledgerData.reconciliationActions.actionsPendingOnVendor.amount))
                          ) : (
                            <WipBadge />
                          )
                        }
                        count={ledgerData.reconciliationActions?.actionsPendingOnVendor.count}
                        onClick={() => {
                          trackReconPendingOnVendorClicked({
                            tile_amount: ledgerData.reconciliationActions?.actionsPendingOnVendor.amount,
                            entry_count: ledgerData.reconciliationActions?.actionsPendingOnVendor.count,
                          });
                          history.push("/vendorPortal/disputed-entries");
                        }}
                      />
                      <Leaf
                        label="Actions Pending on Us"
                        amount={
                          ledgerData.reconciliationActions ? (
                            formatMoney(ToDineroObj(ledgerData.reconciliationActions.actionsPendingOnUs.amount))
                          ) : (
                            <WipBadge />
                          )
                        }
                        count={ledgerData.reconciliationActions?.actionsPendingOnUs.count}
                        onClick={() => {
                          trackReconPendingOnUsClicked({
                            tile_amount: ledgerData.reconciliationActions?.actionsPendingOnUs.amount,
                            entry_count: ledgerData.reconciliationActions?.actionsPendingOnUs.count,
                          });
                          history.push("/vendorPortal/disputed-entries");
                        }}
                      />
                    </Stack>
                  )}
                </CardContent>
              </Card>
            )}

            {expandedSections.needsAction && (
              <Card sx={{ backgroundColor: "#fafafa", border: "1px solid rgba(0,0,0,0.12)", mt: 1.5 }}>
                <CardContent sx={{ p: 1.5 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "baseline",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      setOtherIssuesExpanded((prev) => !prev);
                      trackOtherIssuesToggled(!otherIssuesExpanded);
                    }}
                  >
                    <Typography className="heading-s">Other Issues Raised</Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {ledgerData.otherIssuesRaised ? (
                        <Typography className="heading-s">
                          {formatMoney(
                            ToDineroObj(
                              ledgerData.otherIssuesRaised.actionsPendingOnVendor.amount +
                                ledgerData.otherIssuesRaised.actionsPendingOnUs.amount
                            )
                          )}{" "}
                          (
                          {ledgerData.otherIssuesRaised.actionsPendingOnVendor.count +
                            ledgerData.otherIssuesRaised.actionsPendingOnUs.count}
                          )
                        </Typography>
                      ) : (
                        <WipBadge />
                      )}
                      <ExpandToggle expanded={otherIssuesExpanded} />
                    </Box>
                  </Box>
                  {otherIssuesExpanded && (
                    <Stack direction="row" spacing={1.25} sx={{ mt: 1.5 }}>
                      <Leaf
                        label="Actions Pending on Vendor"
                        amount={
                          ledgerData.otherIssuesRaised ? (
                            formatMoney(ToDineroObj(ledgerData.otherIssuesRaised.actionsPendingOnVendor.amount))
                          ) : (
                            <WipBadge />
                          )
                        }
                        count={ledgerData.otherIssuesRaised?.actionsPendingOnVendor.count}
                        onClick={() => {
                          trackOtherIssuesPendingOnVendorClicked({
                            tile_amount: ledgerData.otherIssuesRaised?.actionsPendingOnVendor.amount,
                            entry_count: ledgerData.otherIssuesRaised?.actionsPendingOnVendor.count,
                          });
                          history.push("/vendorPortal/raise-other-issues");
                        }}
                      />
                      <Leaf
                        label="Actions Pending on Us"
                        amount={
                          ledgerData.otherIssuesRaised ? (
                            formatMoney(ToDineroObj(ledgerData.otherIssuesRaised.actionsPendingOnUs.amount))
                          ) : (
                            <WipBadge />
                          )
                        }
                        count={ledgerData.otherIssuesRaised?.actionsPendingOnUs.count}
                        onClick={() => {
                          trackOtherIssuesPendingOnUsClicked({
                            tile_amount: ledgerData.otherIssuesRaised?.actionsPendingOnUs.amount,
                            entry_count: ledgerData.otherIssuesRaised?.actionsPendingOnUs.count,
                          });
                          history.push("/vendorPortal/raise-other-issues");
                        }}
                      />
                    </Stack>
                  )}
                </CardContent>
              </Card>
            )}
          </CardContent>
        </Card>
      ) : null}
    </Box>
  );
};

export default LedgerSummaryVendorPortal;
